import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { isNil } from 'es-toolkit/compat';

import { TaxSavingsDocument } from './graphql/taxSavings.generated';

import { trackClick } from '../../../../features/track';
import { DateRangeOption } from '../../../../graphql/schema';
import { TaxLossHarvestingPerformanceModal } from '../../../../Shared/components-v2/tax-loss-harvesting/TaxLossHarvestingPerformanceModal';
import { TaxLossHarvestingToggletip } from '../../../../Shared/components-v2/tax-loss-harvesting/TaxLossHarvestingToggletip';
import { urlBuilder } from '../../../../Shared/urlBuilder';
import { type CustomDateRange, dateRangeFilterByValue } from '../../../shared/date-range-filter/dateUtils';

interface TaxSavingsCardViewProps {
  dateRangeOption?: DateRangeOption;
  customDateRange?: CustomDateRange;
}

type TaxLossHarvestingStatus = 'active' | 'inactive' | 'none';

function getTaxLossHarvestingStatus(
  taxLossesHarvested: Money | null | undefined,
  allTimeTaxSavings: Money
): TaxLossHarvestingStatus {
  if (isNil(taxLossesHarvested)) {
    return 'inactive';
  }
  if (taxLossesHarvested.cents > 0 || allTimeTaxSavings.cents > 0) {
    return 'active';
  }
  return 'none';
}

const handleSeeDetailsClick = () => {
  trackClick({
    name: 'tlhActiveSeeDetails',
    module: 'Performance',
    location: 'TaxSavingsCard',
  });
};

const handleHarvestedLossesToolTipOpen = (open: boolean) => {
  if (open) {
    trackClick({
      name: 'harvestedLossesToolTip',
      module: 'Performance',
      location: 'TaxSavingsCard',
    });
  }
};

const handleTlhInactiveExploreClick = () => {
  trackClick({
    name: 'tlhInactiveExplore',
    module: 'Performance',
    location: 'TaxSavingsCard',
  });
};

const TaxSavingsCardView: React.FC<TaxSavingsCardViewProps> = ({
  dateRangeOption = DateRangeOption.AllTime,
  customDateRange,
}) => {
  const { data } = useGraphQLQuery(TaxSavingsDocument, {
    variables: {
      dateRangeOption,
      customDateRange,
    },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const allTimeTaxSavings = data.performance.totals.investing?.allTimeTlhEstimatedTaxSavings || Money(0);
  const taxLossesHarvested = data.performance.totals.investing?.taxLossesHarvested;
  const dateRangeLabel = dateRangeFilterByValue(dateRangeOption)?.prefixedDescription('Harvested') || '';
  const isAllTime = dateRangeOption === DateRangeOption.AllTime;

  const tlhStatus = getTaxLossHarvestingStatus(taxLossesHarvested, allTimeTaxSavings);

  switch (tlhStatus) {
    case 'inactive':
      return (
        <Card>
          <Card.Header css={{ padding: 'space.300' }}>
            <H2 variant="title200">Turn market dips into tax savings</H2>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content css={{ padding: 'space.300', display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
              <Text>
                Tax-loss harvesting automatically sells investments that have lost value, then reinvests the money to
                help lower your taxes.
              </Text>
              <SambaAutoLink
                to={urlBuilder.appTlhSetupSuitabilityIntroduction.absolute()}
                variant="textPrimary"
                onClick={handleTlhInactiveExploreClick}
              >
                See if it's right for you
              </SambaAutoLink>
            </Box>
          </Card.Content>
        </Card>
      );

    case 'none':
      return (
        <Card>
          <Card.Header css={{ padding: 'space.300' }}>
            <H2 variant="title200">Tax savings</H2>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content css={{ padding: 'space.300' }}>
            <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Text>Tax losses harvesting</Text>
                <TaxLossHarvestingToggletip isEnabled={true} onOpenChange={handleHarvestedLossesToolTipOpen} />
              </Box>
              <Text>Active</Text>
            </Box>
          </Card.Content>
        </Card>
      );

    case 'active':
      return (
        <Card>
          <Card.Header css={{ padding: 'space.300' }}>
            <H2 variant="title200">Tax savings</H2>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content css={{ padding: 'space.300', display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
            <Box css={{ display: 'flex', flexDirection: 'column' }}>
              <Text>Estimated taxes saved{!isAllTime && ' (all time)'}</Text>
              <Text variant="text300" css={{ color: 'text.success' }}>
                {formatMoney(allTimeTaxSavings)}
              </Text>
            </Box>
            <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Text>{isAllTime ? 'Tax losses harvested' : dateRangeLabel}</Text>
                <TaxLossHarvestingToggletip isEnabled={true} onOpenChange={handleHarvestedLossesToolTipOpen} />
              </Box>
              <Text>{formatMoney(taxLossesHarvested || Money(0))}</Text>
            </Box>
          </Card.Content>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Footer css={{ padding: 'space.300' }}>
            <TaxLossHarvestingPerformanceModal onOpenClick={handleSeeDetailsClick} />
          </Card.Footer>
        </Card>
      );
    default:
      return null;
  }
};

export const TaxSavingsCard: React.FC<TaxSavingsCardViewProps> = props => (
  <ErrorBoundary fallbackType="none">
    <TaxSavingsCardView {...props} />
  </ErrorBoundary>
);
